import { AWSLogo } from "@/assets/logos/AWSLogo";
import { AzureLogo } from "@/assets/logos/AzureLogo";
import { GCPLogo } from "@/assets/logos/GCPLogo";

import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { NewAWSRunnerForm } from "@/components/runners/NewAWSRunnerForm";
import { NewGCPRunnerForm } from "@/components/runners/NewGCPRunnerForm";

import { ProviderOption } from "@/components/runners/ProviderOption";
import { type RunnerTemplate } from "@/components/runners/RunnerTemplateSelection";
import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { TrackLocations, useSegmentTrack } from "@/hooks/use-segment";
import { useOrganization } from "@/queries/organization-queries";
import { useCreateRemoteRunner } from "@/queries/runner-queries";
import { formatError } from "@/utils/errors";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerVariant } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useCallback, useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
    open: boolean;
    onClose: () => void;
};

type ModalState = {
    screen: "provider-selection" | "template-selection" | "configuration";
    selectedProvider?: RunnerProvider;
    selectedTemplate?: RunnerTemplate;
};

export const NewRunnerModal: FC<Props> = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const track = useSegmentTrack();
    const { data: organization } = useOrganization();
    const createRunner = useCreateRemoteRunner();
    const { value: isGcpRunnerEnabled } = useFlag(Flags.IsGcpRunnerEnabled, false);
    const [disabled, setDisabled] = useState(false);
    const [modalState, setModalState] = useState<ModalState>({
        screen: "provider-selection",
    });

    // Reset the modal state when the modal is opened
    useEffect(() => {
        if (open) {
            setModalState({
                screen: "provider-selection",
            });
        }
    }, [open]);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const isEnterpriseUser = organization?.tier === OrganizationTier.ENTERPRISE;
    const isNonEnterpriseTier =
        organization?.tier === OrganizationTier.FREE_ONA ||
        organization?.tier === OrganizationTier.FREE ||
        organization?.tier === OrganizationTier.CORE;

    const handleProviderSelect = useCallback(
        (provider: RunnerProvider) => {
            // For non-Enterprise tiers, emit trial_requested event and close modal
            if (isNonEnterpriseTier) {
                track("trial_requested", {
                    provider: provider,
                    tier: organization?.tier,
                });
                toast({
                    title: "Enterprise trial requested",
                    description: "Your request for an Enterprise trial has been submitted. We'll be in touch soon!",
                });
                onClose();
                return;
            }

            // For Enterprise tier, proceed directly to configuration for AWS (no template selection)
            if (provider === RunnerProvider.AWS_EC2 && isEnterpriseUser) {
                setModalState({
                    screen: "configuration",
                    selectedProvider: provider,
                    selectedTemplate: "enterprise", // Enterprise users get Enterprise runners
                });
            } else {
                setModalState({ screen: "configuration", selectedProvider: provider });
            }
        },
        [isNonEnterpriseTier, isEnterpriseUser, organization?.tier, track, toast, onClose],
    );

    const handleBack = useCallback(() => {
        // Always go back to provider selection since we removed template selection
        setModalState({ screen: "provider-selection" });
    }, []);

    const handleSubmit = useCallback<
        (values: { name: string; region?: string; provider: RunnerProvider }) => Promise<void>
    >(
        async ({ name, region, provider }) => {
            let runnerVariant = undefined;
            if (provider === RunnerProvider.AWS_EC2) {
                // For Enterprise tier, always create Enterprise runners
                runnerVariant = RunnerVariant.ENTERPRISE;
            } else if (provider === RunnerProvider.GCP) {
                runnerVariant = RunnerVariant.ENTERPRISE;
            }

            try {
                const createRunnerParams: {
                    name: string;
                    region?: string;
                    provider: RunnerProvider;
                    variant?: RunnerVariant;
                } = {
                    name,
                    provider,
                    variant: runnerVariant,
                };

                // Only include region if it's provided (AWS needs it, Linux/GCP don't)
                if (region) {
                    createRunnerParams.region = region;
                }

                const { runner } = await createRunner.mutateAsync(createRunnerParams);
                navigate(`/settings/runners/${runner.runnerId}?showOnboarding=true`);
            } catch (error) {
                toast({ title: "Failed to create a runner", description: formatError(error) });
            }
        },
        [createRunner, navigate, toast],
    );

    const modalTitle = (modalState: ModalState) => {
        if (modalState.screen === "provider-selection") {
            return "Add a runner";
        }

        switch (modalState.selectedProvider) {
            case RunnerProvider.AWS_EC2:
                return "Create AWS Enterprise runner";
            case RunnerProvider.GCP:
                return "Create GCP runner";
            default:
                return "";
        }
    };

    const modalDescription = (modalState: ModalState) => {
        switch (modalState.screen) {
            case "provider-selection":
                return "Choose which provider to use for running your environments";
            default:
                return "";
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-[600px]" data-track-location={TrackLocations.NewRunnerModal}>
                <DialogHeader>
                    <DialogTitle>{modalTitle(modalState)}</DialogTitle>
                    <DialogDescription>{modalDescription(modalState)}</DialogDescription>
                </DialogHeader>

                <DialogBody>
                    {modalState.screen === "provider-selection" && (
                        <div className="flex flex-col gap-2">
                            <ProviderOption
                                name="AWS"
                                Logo={AWSLogo}
                                label={isNonEnterpriseTier ? "Request Enterprise Trial" : "Select"}
                                onClick={() => handleProviderSelect(RunnerProvider.AWS_EC2)}
                                data-tracking-id="select-aws-provider-option"
                            />
                            <ProviderOption
                                name="GCP"
                                Logo={GCPLogo}
                                label={
                                    isGcpRunnerEnabled
                                        ? isNonEnterpriseTier
                                            ? "Request Enterprise Trial"
                                            : "Select"
                                        : "Contact sales"
                                }
                                onClick={
                                    isGcpRunnerEnabled ? () => handleProviderSelect(RunnerProvider.GCP) : undefined
                                }
                                href={!isGcpRunnerEnabled ? "https://ona.com/contact/sales" : undefined}
                                data-tracking-id="select-gcp-provider-option"
                                variant={isGcpRunnerEnabled ? "secondary" : "link"}
                            />
                            <ProviderOption
                                name="Azure"
                                Logo={AzureLogo}
                                label="Join waitlist"
                                data-tracking-id="join-azure-waitlist-provider-option"
                            />
                        </div>
                    )}

                    {modalState.screen === "configuration" &&
                        modalState.selectedProvider === RunnerProvider.AWS_EC2 && (
                            <NewAWSRunnerForm
                                formId="new-runner-form"
                                onSubmit={(values) =>
                                    handleSubmit({
                                        ...values,
                                        provider: RunnerProvider.AWS_EC2,
                                    })
                                }
                                onDisabledChange={setDisabled}
                            />
                        )}

                    {modalState.screen === "configuration" && modalState.selectedProvider === RunnerProvider.GCP && (
                        <NewGCPRunnerForm
                            formId="new-runner-form"
                            onSubmit={(values) => handleSubmit({ ...values, provider: RunnerProvider.GCP })}
                            onDisabledChange={setDisabled}
                        />
                    )}
                </DialogBody>

                {modalState.screen === "configuration" && (
                    <DialogFooter>
                        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-between">
                            <div>
                                <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={handleBack}
                                    data-tracking-id="back-new-runner-modal"
                                >
                                    Back
                                </Button>
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                                <Button
                                    loading={createRunner.isPending}
                                    autoFocus={true}
                                    aria-label="create"
                                    type="submit"
                                    data-testid="create"
                                    form="new-runner-form"
                                    disabled={disabled}
                                    data-tracking-id="confirm-create-runner-new-runner-modal"
                                >
                                    Create
                                </Button>
                            </div>
                        </div>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};
